/**
 * ProgramRow Logo
 *
 * The mark is the switcher-bus concept from the design bundle (option 5A):
 * two rows of four square buttons, one lit red on the program row and one lit
 * green on the preview row. It is pure geometry, so it stays crisp at every
 * size and needs no raster asset in the app itself.
 *
 * Geometry (unit = one button, gap = 0.5 unit) scales linearly:
 *   width  = 4 units + 3 gaps = 5.5 units
 *   height = 2 units + 1 gap  = 2.5 units
 * Drawn here with unit = 2 and gap = 1 so every coordinate is an integer.
 */

const UNIT = 2;
const GAP = 1;
const STEP = UNIT + GAP; // 3
const VIEW_W = 4 * UNIT + 3 * GAP; // 11
const VIEW_H = 2 * UNIT + GAP; // 5

const PROGRAM_RED = '#E5342A';
const PREVIEW_GREEN = '#2FBF6B';
const UNLIT_ON_DARK = '#3A3A3F';
const UNLIT_ON_LIGHT = '#D4D4D2';

/** Row 1 lights position 2 (red); row 2 lights position 4 (green). */
const LIT: Record<string, string> = {
  '0:1': PROGRAM_RED,
  '1:3': PREVIEW_GREEN,
};

export interface LogoMarkProps {
  /** Rendered height of the mark in px (width follows the 11:5 ratio) */
  size?: number;
  /** Which surface the mark sits on — decides the unlit button colour */
  on?: 'dark' | 'light';
  className?: string;
}

export function LogoMark({ size = 20, on = 'dark', className = '' }: LogoMarkProps) {
  const unlit = on === 'dark' ? UNLIT_ON_DARK : UNLIT_ON_LIGHT;
  const buttons = [];

  for (let row = 0; row < 2; row++) {
    for (let col = 0; col < 4; col++) {
      buttons.push(
        <rect
          key={`${row}:${col}`}
          x={col * STEP}
          y={row * STEP}
          width={UNIT}
          height={UNIT}
          fill={LIT[`${row}:${col}`] ?? unlit}
        />
      );
    }
  }

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      height={size}
      width={(size * VIEW_W) / VIEW_H}
      role="img"
      aria-label="ProgramRow"
      focusable="false"
      className={className}
      shapeRendering="crispEdges"
    >
      {buttons}
    </svg>
  );
}

export interface LogoProps extends LogoMarkProps {
  /** Render the "ProgramRow" wordmark beside the mark (default: true) */
  wordmark?: boolean;
  /** Wordmark font size in px (defaults to 1.1x the mark height) */
  wordmarkSize?: number;
}

/**
 * Mark plus wordmark. The gap is 0.75x the mark height per the bundle, so the
 * lockup keeps its proportions at any size.
 */
export default function Logo({
  size = 20,
  on = 'dark',
  wordmark = true,
  wordmarkSize,
  className = '',
}: LogoProps) {
  if (!wordmark) {
    return <LogoMark size={size} on={on} className={className} />;
  }

  return (
    <span
      className={`inline-flex items-center ${className}`}
      style={{ gap: `${size * 0.75}px` }}
    >
      <LogoMark size={size} on={on} />
      <span
        className="font-semibold whitespace-nowrap"
        style={{
          fontSize: wordmarkSize ?? size * 1.1,
          letterSpacing: '-0.03em',
          lineHeight: 1,
        }}
      >
        <span style={{ color: on === 'dark' ? '#FFFFFF' : '#17171A' }}>Program</span>
        <span style={{ color: on === 'dark' ? '#8D8D90' : '#76767A' }}>Row</span>
      </span>
    </span>
  );
}
